import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Button,
  SegmentedControl,
  Box,
  Badge,
  Code,
  ScrollArea,
  ActionIcon,
  Tooltip,
  Modal,
  Slider,
} from '@mantine/core';
import {
  IconRefresh,
  IconTrash,
  IconTerminal2,
  IconSend,
  IconAppWindow,
  IconWorld,
  IconMaximize,
  IconZoomIn,
  IconZoomOut,
} from '@tabler/icons-react';
import { GetWebBotBundleUrl } from '@/APIURL';
import { SpinePreview } from '../SpinePreview';
import {
  ExtensionManifestFormValues,
  ExtensionTemplateType,
  SimulatedState,
  StateGroupConfig,
  pickWeightedItem,
  WeightedActionItem,
} from './types';

interface ExtensionPreviewProps {
  botProviderId: string;
  bundleName: string;
  templateType: ExtensionTemplateType;
  formValues: ExtensionManifestFormValues;
  onAnimationsLoaded?: (animations: string[]) => void;
}

interface IframeEventLog {
  id: string;
  time: string;
  type: string;
  payload?: any;
}

function parseDimensionPx(val?: string | number, fallback: number = 360): number {
  if (typeof val === 'number') return val > 0 ? val : fallback;
  if (!val) return fallback;
  const parsed = parseFloat(val);
  return isNaN(parsed) || parsed <= 0 ? fallback : parsed;
}

const PREVIEW_CONTAINER_HEIGHT = 300;

export function ExtensionPreview({
  botProviderId,
  bundleName,
  templateType,
  formValues,
  onAnimationsLoaded,
}: ExtensionPreviewProps) {
  const [simulatedState, setSimulatedState] = useState<SimulatedState>('idle');
  const [replayTrigger, setReplayTrigger] = useState(0);
  const [eventLogs, setEventLogs] = useState<IframeEventLog[]>([]);
  const [zoomPercent, setZoomPercent] = useState<number | null>(null);
  const [expandModalOpened, setExpandModalOpened] = useState(false);

  const lastNonClickStateRef = useRef<SimulatedState>('idle');

  useEffect(() => {
    if (simulatedState !== 'click') {
      lastNonClickStateRef.current = simulatedState;
    }
  }, [simulatedState]);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const expandIframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: PREVIEW_CONTAINER_HEIGHT });

  // Monitor container size changes for fit-to-view auto scaling
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [templateType]);

  // Dimension helpers
  const parsedFormHeight = useMemo(() => parseDimensionPx(formValues.height, 360), [formValues.height]);
  const parsedFormWidth = useMemo(() => parseDimensionPx(formValues.width, 280), [formValues.width]);
  const isPercentageWidth = useMemo(
    () => typeof formValues.width === 'string' && formValues.width.trim().endsWith('%'),
    [formValues.width]
  );

  // Custom HTML entry file and URL
  const customHtmlSrc = useMemo(() => {
    if (templateType !== 'custom-html') return '';
    const entry = formValues.entryHtml?.trim() || 'index.html';
    return GetWebBotBundleUrl(botProviderId, `${bundleName}/${entry}`);
  }, [templateType, botProviderId, bundleName, formValues.entryHtml]);

  // Dispatch state change to the preview iframe
  const broadcastStateToIframe = useCallback(() => {
    if (templateType !== 'custom-html') return;
    const payload = {
      type: 'cakechat:state-change',
      payload: {
        state: simulatedState === 'speaking' ? 'streaming' : simulatedState,
        isThinking: simulatedState === 'thinking',
        isStreaming: simulatedState === 'speaking',
      },
    };

    const sendTo = (iframeEl: HTMLIFrameElement | null) => {
      if (!iframeEl?.contentWindow) return;
      try {
        iframeEl.contentWindow.postMessage(payload, '*');
      } catch (err) {
        console.warn('Failed to broadcast simulated state to iframe:', err);
      }
    };

    sendTo(iframeRef.current);
    sendTo(expandIframeRef.current);
  }, [templateType, simulatedState]);

  useEffect(() => {
    broadcastStateToIframe();
  }, [broadcastStateToIframe]);

  // Listen to postMessage events from the preview iframe
  useEffect(() => {
    if (templateType !== 'custom-html') return;

    const handleMessage = (e: MessageEvent) => {
      if (!e.data || typeof e.data !== 'object') return;
      const { type, payload } = e.data;
      if (!type || typeof type !== 'string' || !type.startsWith('cakechat:')) return;

      const now = new Date();
      const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;

      setEventLogs((prev) => [
        {
          id: `${Date.now()}_${Math.random()}`,
          time: timeStr,
          type,
          payload,
        },
        ...prev.slice(0, 19), // Keep latest 20 logs
      ]);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [templateType]);

  // Active state group configs
  const activeImageStateConfig = useMemo((): StateGroupConfig => {
    const { imageIdleConfig, imageSleepConfig, imageThinkingConfig, imageSpeakingConfig, imageErrorConfig, imageClickConfig } = formValues;
    if (simulatedState === 'sleep') return imageSleepConfig?.items?.length ? imageSleepConfig : imageIdleConfig;
    if (simulatedState === 'thinking') return imageThinkingConfig?.items?.length ? imageThinkingConfig : imageIdleConfig;
    if (simulatedState === 'speaking') return imageSpeakingConfig?.items?.length ? imageSpeakingConfig : imageIdleConfig;
    if (simulatedState === 'error') return imageErrorConfig?.items?.length ? imageErrorConfig : imageIdleConfig;
    if (simulatedState === 'click') return imageClickConfig?.items?.length ? imageClickConfig : imageIdleConfig;
    return imageIdleConfig;
  }, [simulatedState, formValues]);

  const [activeImageItem, setActiveImageItem] = useState<WeightedActionItem | null>(null);

  // Rotation effect for Image States preview
  useEffect(() => {
    if (templateType !== 'image-states') return;

    const picked = pickWeightedItem(activeImageStateConfig.items || []);
    setActiveImageItem(picked);

    if (activeImageStateConfig.strategy === 'on-complete' && (activeImageStateConfig.items || []).length > 1) {
      const intervalSec = picked?.interval || activeImageStateConfig.interval || 5;
      const timer = setInterval(() => {
        const next = pickWeightedItem(activeImageStateConfig.items || []);
        setActiveImageItem(next);
      }, Math.max(1000, intervalSec * 1000));

      return () => clearInterval(timer);
    }
  }, [templateType, activeImageStateConfig, replayTrigger]);

  const activePreviewImage = useMemo(() => {
    if (templateType !== 'image-states') return null;
    const file = activeImageItem?.animation;
    if (!file) return null;
    return GetWebBotBundleUrl(botProviderId, `${bundleName}/${file}`);
  }, [templateType, activeImageItem, bundleName, botProviderId]);

  // Preview helper for Spine animations
  const activeSpineStateConfig = useMemo((): StateGroupConfig => {
    const { spineIdleConfig, spineSleepConfig, spineThinkingConfig, spineSpeakingConfig, spineErrorConfig, spineClickConfig } = formValues;
    if (simulatedState === 'sleep') return spineSleepConfig?.items?.length ? spineSleepConfig : spineIdleConfig;
    if (simulatedState === 'thinking') return spineThinkingConfig?.items?.length ? spineThinkingConfig : spineIdleConfig;
    if (simulatedState === 'speaking') return spineSpeakingConfig?.items?.length ? spineSpeakingConfig : spineIdleConfig;
    if (simulatedState === 'error') return spineErrorConfig?.items?.length ? spineErrorConfig : spineIdleConfig;
    if (simulatedState === 'click') return spineClickConfig?.items?.length ? spineClickConfig : spineIdleConfig;
    return spineIdleConfig;
  }, [simulatedState, formValues]);

  // Automatically return back to previous state / idle after click interaction
  useEffect(() => {
    if (simulatedState !== 'click') return;

    if (templateType === 'image-states') {
      const duration = (formValues.imageClickConfig?.interval || 1.5) * 1000;
      const timer = setTimeout(() => {
        setSimulatedState(lastNonClickStateRef.current || 'idle');
      }, Math.max(500, duration));
      return () => clearTimeout(timer);
    }

    if (templateType === 'spine2d') {
      // Safety fallback timer if Spine complete listener doesn't fire
      const timer = setTimeout(() => {
        setSimulatedState(lastNonClickStateRef.current || 'idle');
      }, 3000);
      return () => clearTimeout(timer);
    }

    if (templateType === 'custom-html') {
      const timer = setTimeout(() => {
        setSimulatedState(lastNonClickStateRef.current || 'idle');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [templateType, simulatedState, formValues.imageClickConfig?.interval]);

  // Scaling calculation for fit view mode (keeps inside PREVIEW_CONTAINER_HEIGHT)
  const targetContentW = isPercentageWidth
    ? (containerSize.width > 0 ? containerSize.width : 280)
    : parsedFormWidth;
  const targetContentH = parsedFormHeight;

  const calculatedFitScale = useMemo(() => {
    const availableW = Math.max(containerSize.width - 32, 50);
    const availableH = Math.max(PREVIEW_CONTAINER_HEIGHT - 32, 50);
    if (targetContentW <= 0 || targetContentH <= 0) return 1;

    const scaleX = availableW / targetContentW;
    const scaleY = availableH / targetContentH;
    return Math.min(scaleX, scaleY, 1);
  }, [containerSize.width, targetContentW, targetContentH]);

  const calculatedFitPercent = useMemo(() => {
    return Math.max(25, Math.min(200, Math.round(calculatedFitScale * 100)));
  }, [calculatedFitScale]);

  const currentZoom = zoomPercent ?? calculatedFitPercent;
  const scaleFactor = currentZoom / 100;
  const scaledW = targetContentW * scaleFactor;
  const scaledH = targetContentH * scaleFactor;

  return (
    <>
      <Paper withBorder p="sm" radius="md" style={{ background: 'var(--mantine-color-gray-0)' }}>
        <Stack gap="xs" align="center">
          <Group justify="space-between" w="100%" px="xs">
            <Group gap="xs">
              <Text size="xs" fw={600} c="dimmed">
                即時狀態切換模擬預覽
              </Text>
              {templateType === 'custom-html' && (
                <Badge size="xs" variant="light" color="indigo" leftSection={<IconWorld size={12} />}>
                  自訂網頁 Iframe 沙盒
                </Badge>
              )}
            </Group>

            <Group gap="xs">
              {templateType === 'custom-html' && (
                <Tooltip label="放大全螢幕檢視">
                  <ActionIcon
                    variant="light"
                    color="indigo"
                    size="sm"
                    onClick={() => setExpandModalOpened(true)}
                  >
                    <IconMaximize size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Button
                variant="subtle"
                size="compact-xs"
                leftSection={<IconRefresh size={13} />}
                onClick={() => {
                  setReplayTrigger((prev) => prev + 1);
                  if (templateType === 'custom-html' && iframeRef.current) {
                    iframeRef.current.src = customHtmlSrc;
                  }
                }}
              >
                {templateType === 'custom-html' ? '重新載入' : '重新播放'}
              </Button>
            </Group>
          </Group>

          <SegmentedControl
            size="xs"
            value={simulatedState}
            onChange={(val) => {
              setSimulatedState(val as SimulatedState);
              setReplayTrigger((prev) => prev + 1);
            }}
            data={[
              { label: '待機', value: 'idle' },
              { label: '休眠', value: 'sleep' },
              { label: '思考中', value: 'thinking' },
              { label: '回應中', value: 'speaking' },
              { label: '錯誤', value: 'error' },
              { label: '點擊', value: 'click' },
            ]}
          />

          <Text size="xs" c="dimmed">
            {templateType === 'custom-html'
              ? '💡 切換狀態將透過 postMessage 派發 cakechat:state-change 給下方 Iframe'
              : '💡 點擊下方角色預覽畫面亦可即時觸發與重播點擊動作'}
          </Text>

          {templateType === 'spine2d' && (
            <SpinePreview
              botProviderId={botProviderId}
              bundleName={bundleName}
              skeletonFile={formValues.skeletonFile}
              atlasFile={formValues.atlasFile}
              stateConfig={activeSpineStateConfig}
              loop={simulatedState !== 'click'}
              height={180}
              replayTrigger={replayTrigger}
              onClick={() => {
                setSimulatedState('click');
                setReplayTrigger((prev) => prev + 1);
              }}
              onComplete={() => {
                if (simulatedState === 'click') {
                  setSimulatedState(lastNonClickStateRef.current || 'idle');
                }
              }}
              onAnimationsLoaded={onAnimationsLoaded}
            />
          )}

          {templateType === 'image-states' && (
            <Box
              onClick={() => {
                setSimulatedState('click');
                setReplayTrigger((prev) => prev + 1);
              }}
              style={{
                height: '140px',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              {activePreviewImage ? (
                <img
                  src={activePreviewImage}
                  alt="Preview"
                  style={{ maxHeight: '120px', maxWidth: '100%', objectFit: 'contain' }}
                />
              ) : (
                <Text size="xs" c="dimmed">
                  未指定該狀態之圖片
                </Text>
              )}
            </Box>
          )}

          {templateType === 'custom-html' && (
            <Stack gap="xs" w="100%">
              {/* Controls Toolbar: Custom Zoom Slider & Quick Reset Buttons */}
              <Group justify="space-between" align="center" px={4} wrap="wrap" gap="xs">
                <Group gap={6} align="center">
                  <Text size="xs" c="dimmed" fw={500}>
                    縮放比例:
                  </Text>
                  <Tooltip label="縮小 10%">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="xs"
                      onClick={() => setZoomPercent(Math.max(25, currentZoom - 10))}
                      disabled={currentZoom <= 25}
                    >
                      <IconZoomOut size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Slider
                    size="xs"
                    w={110}
                    min={25}
                    max={200}
                    step={5}
                    value={currentZoom}
                    onChange={(val) => setZoomPercent(val)}
                    label={(val) => `${val}%`}
                  />
                  <Tooltip label="放大 10%">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="xs"
                      onClick={() => setZoomPercent(Math.min(200, currentZoom + 10))}
                      disabled={currentZoom >= 200}
                    >
                      <IconZoomIn size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Badge size="xs" variant="light" color="indigo" w={46} style={{ textAlign: 'center' }}>
                    {currentZoom}%
                  </Badge>
                </Group>

                <Group gap={6} align="center">
                  <Button
                    variant={currentZoom === calculatedFitPercent ? 'light' : 'subtle'}
                    color="indigo"
                    size="compact-xs"
                    onClick={() => setZoomPercent(calculatedFitPercent)}
                  >
                    🎯 最佳適配 ({calculatedFitPercent}%)
                  </Button>
                  <Button
                    variant={currentZoom === 100 ? 'light' : 'subtle'}
                    color="gray"
                    size="compact-xs"
                    onClick={() => setZoomPercent(100)}
                  >
                    100% 原生
                  </Button>
                </Group>
              </Group>

              {/* Viewport Dimensions hint */}
              <Group justify="space-between" px={4}>
                <Text size="11px" c="dimmed">
                  📐 虛擬解析度: {parsedFormWidth} × {parsedFormHeight}px
                </Text>
                {currentZoom > calculatedFitPercent && (
                  <Text size="11px" c="dimmed">
                    💡 超出預覽框部分可透過捲軸滾動檢視
                  </Text>
                )}
              </Group>

              {/* Iframe Viewport Container with Fixed Height & Canvas Positioning */}
              <Box
                ref={containerRef}
                style={{
                  width: '100%',
                  height: `${PREVIEW_CONTAINER_HEIGHT}px`,
                  border: '1px solid var(--mantine-color-gray-3)',
                  borderRadius: '6px',
                  overflow: 'auto',
                  background:
                    'repeating-conic-gradient(#f8f9fa 0% 25%, #ffffff 0% 50%) 50% / 20px 20px',
                  position: 'relative',
                  display: 'flex',
                }}
              >
                <Box
                  style={{
                    margin: 'auto',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '100%',
                    minHeight: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    style={{
                      width: `${scaledW}px`,
                      height: `${scaledH}px`,
                      position: 'relative',
                      flexShrink: 0,
                      boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      background: '#ffffff',
                    }}
                  >
                    <Box
                      style={{
                        width: `${targetContentW}px`,
                        height: `${targetContentH}px`,
                        transform: `scale(${scaleFactor})`,
                        transformOrigin: 'top left',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                      }}
                    >
                      <iframe
                        key={`${customHtmlSrc}_${replayTrigger}`}
                        ref={iframeRef}
                        src={customHtmlSrc}
                        title="Custom HTML Extension Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          background: 'transparent',
                        }}
                        sandbox="allow-scripts allow-same-origin allow-popups"
                        onLoad={() => {
                          broadcastStateToIframe();
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* PostMessage Events Monitor */}
              <Paper withBorder p="xs" radius="sm" bg="white" w="100%">
                <Group justify="space-between" mb={4}>
                  <Group gap={6}>
                    <IconTerminal2 size={14} color="var(--mantine-color-blue-6)" />
                    <Text size="xs" fw={600}>
                      Iframe 事件監聽日誌 (postMessage Monitor)
                    </Text>
                  </Group>
                  {eventLogs.length > 0 && (
                    <Tooltip label="清除日誌">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="xs"
                        onClick={() => setEventLogs([])}
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>

                {eventLogs.length === 0 ? (
                  <Text size="xs" c="dimmed" ta="center" py={4}>
                    尚無事件。在 Iframe 內觸發動作時將在此即時顯示。
                  </Text>
                ) : (
                  <ScrollArea.Autosize mah={90}>
                    <Stack gap={4}>
                      {eventLogs.map((log) => {
                        let typeColor = 'blue';
                        let Icon = IconSend;
                        if (log.type === 'cakechat:send-message') {
                          typeColor = 'teal';
                          Icon = IconSend;
                        } else if (
                          log.type.startsWith('cakechat:open') ||
                          log.type.startsWith('cakechat:close') ||
                          log.type.startsWith('cakechat:toggle')
                        ) {
                          typeColor = 'grape';
                          Icon = IconAppWindow;
                        }

                        return (
                          <Group key={log.id} justify="space-between" wrap="nowrap" gap="xs">
                            <Group gap={4} wrap="nowrap">
                              <Text size="10px" c="dimmed">
                                {log.time}
                              </Text>
                              <Badge
                                size="xs"
                                color={typeColor}
                                variant="light"
                                leftSection={<Icon size={10} />}
                              >
                                {log.type}
                              </Badge>
                            </Group>
                            {log.payload && (
                              <Code style={{ fontSize: '10px', maxWidth: '60%' }}>
                                {JSON.stringify(log.payload)}
                              </Code>
                            )}
                          </Group>
                        );
                      })}
                    </Stack>
                  </ScrollArea.Autosize>
                )}
              </Paper>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Expanded Maximize Modal */}
      {expandModalOpened && (
        <Modal
          opened={expandModalOpened}
          onClose={() => setExpandModalOpened(false)}
          title={
            <Group gap="xs">
              <IconWorld size={18} color="var(--mantine-color-indigo-6)" />
              <Text fw={600}>自訂網頁 Iframe 放大模擬預覽 — {bundleName}</Text>
            </Group>
          }
          size="90%"
          centered
        >
          <Stack gap="sm">
            <Group justify="space-between">
              <SegmentedControl
                size="xs"
                value={simulatedState}
                onChange={(val) => {
                  setSimulatedState(val as SimulatedState);
                  setReplayTrigger((prev) => prev + 1);
                }}
                data={[
                  { label: '待機', value: 'idle' },
                  { label: '休眠', value: 'sleep' },
                  { label: '思考中', value: 'thinking' },
                  { label: '回應中', value: 'speaking' },
                  { label: '錯誤', value: 'error' },
                  { label: '點擊', value: 'click' },
                ]}
              />
              <Button
                variant="subtle"
                size="compact-xs"
                leftSection={<IconRefresh size={13} />}
                onClick={() => {
                  setReplayTrigger((prev) => prev + 1);
                  if (expandIframeRef.current) {
                    expandIframeRef.current.src = customHtmlSrc;
                  }
                }}
              >
                重新載入
              </Button>
            </Group>

            <Box
              style={{
                width: '100%',
                height: '70vh',
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: '6px',
                overflow: 'hidden',
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <iframe
                key={`modal_${customHtmlSrc}_${replayTrigger}`}
                ref={expandIframeRef}
                src={customHtmlSrc}
                title="Custom HTML Extension Modal Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  background: 'transparent',
                }}
                sandbox="allow-scripts allow-same-origin allow-popups"
                onLoad={() => {
                  broadcastStateToIframe();
                }}
              />
            </Box>
          </Stack>
        </Modal>
      )}
    </>
  );
}
